import React, { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { toast } from "sonner";
import { LogOut, Plus, Trash2, Edit3, Save, X, FileText, Newspaper, Mail, Users } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiError } from "@/lib/api";

const EMPTY = {
    title: "", excerpt: "", content: "", category: "", author: "",
    cover_image: "", read_minutes: 5, published: true,
};

export default function AdminDashboard() {
    const { user, loading, logout } = useAuth();
    const [tab, setTab] = useState("blog");
    const [posts, setPosts] = useState([]);
    const [enquiries, setEnquiries] = useState([]);
    const [subs, setSubs] = useState([]);
    const [editing, setEditing] = useState(null); // {mode:'create'|'edit', post?, form}

    const fetchPosts = async (type) => {
        try {
            const { data } = await api.get("/admin/posts", { params: { type } });
            setPosts(data);
        } catch { setPosts([]); }
    };

    useEffect(() => {
        if (!user) return;
        if (tab === "blog" || tab === "news") fetchPosts(tab);
        if (tab === "enquiries") api.get("/contact/submissions").then((r) => setEnquiries(r.data)).catch(() => setEnquiries([]));
        if (tab === "newsletter") api.get("/newsletter/subscribers").then((r) => setSubs(r.data)).catch(() => setSubs([]));
    }, [tab, user]);

    if (loading) return null;
    if (!user) return <Navigate to="/login" replace />;
    if (user.role !== "admin") return <Navigate to="/" replace />;

    const startCreate = () => setEditing({ mode: "create", form: { ...EMPTY } });
    const startEdit = (post) => setEditing({ mode: "edit", post, form: { ...post } });
    const cancel = () => setEditing(null);

    const save = async () => {
        const f = editing.form;
        try {
            if (editing.mode === "create") {
                await api.post("/admin/posts", f, { params: { type: tab } });
                toast.success("Article created.");
            } else {
                await api.patch(`/admin/posts/${editing.post.id}`, f);
                toast.success("Article updated.");
            }
            setEditing(null);
            fetchPosts(tab);
        } catch (e) {
            toast.error(formatApiError(e));
        }
    };

    const remove = async (post) => {
        if (!window.confirm(`Delete "${post.title}"?`)) return;
        try {
            await api.delete(`/admin/posts/${post.id}`);
            toast.success("Article deleted.");
            fetchPosts(tab);
        } catch (e) { toast.error(formatApiError(e)); }
    };

    const isPost = tab === "blog" || tab === "news";

    return (
        <div data-testid="admin-dashboard" className="min-h-[calc(100vh-5rem)] bg-dr-grey">
            <div className="dr-container py-14">
                <div className="flex flex-wrap items-center justify-between gap-4 mb-10">
                    <div>
                        <p className="text-xs tracking-[0.28em] uppercase text-dr-blue font-medium">Admin console</p>
                        <h1 className="font-display text-4xl font-bold text-dr-navy mt-2">Dynamic Risks CMS</h1>
                        <p className="text-dr-dark/70 mt-1 text-sm">Signed in as {user.email}</p>
                    </div>
                    <button
                        data-testid="admin-logout"
                        onClick={logout}
                        className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full border border-dr-silver bg-white text-dr-navy text-sm hover:bg-dr-navy hover:text-white transition-colors"
                    >
                        <LogOut className="w-4 h-4" /> Sign out
                    </button>
                </div>

                <div className="flex flex-wrap gap-2 mb-8">
                    {[
                        { k: "blog", label: "Blog", icon: FileText },
                        { k: "news", label: "News", icon: Newspaper },
                        { k: "enquiries", label: "Enquiries", icon: Mail },
                        { k: "newsletter", label: "Newsletter", icon: Users },
                    ].map((t) => (
                        <button
                            key={t.k}
                            data-testid={`admin-tab-${t.k}`}
                            onClick={() => { setTab(t.k); setEditing(null); }}
                            className={`inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm border transition-colors ${
                                tab === t.k
                                    ? "bg-dr-navy text-white border-dr-navy"
                                    : "bg-white text-dr-navy border-dr-silver hover:border-dr-navy"
                            }`}
                        >
                            <t.icon className="w-4 h-4" /> {t.label}
                        </button>
                    ))}
                </div>

                {isPost && !editing && (
                    <>
                        <div className="flex justify-end mb-4">
                            <button
                                data-testid="admin-new-post"
                                onClick={startCreate}
                                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-dr-navy text-white text-sm hover:bg-dr-blue transition-colors"
                            >
                                <Plus className="w-4 h-4" /> New {tab === "blog" ? "insight" : "news item"}
                            </button>
                        </div>
                        <div className="bg-white border border-dr-silver rounded-2xl overflow-hidden">
                            <table className="w-full text-sm">
                                <thead>
                                    <tr className="text-left bg-dr-grey/60 text-xs uppercase tracking-widest text-dr-dark/60">
                                        <th className="px-6 py-4">Title</th>
                                        <th className="px-6 py-4">Category</th>
                                        <th className="px-6 py-4">Author</th>
                                        <th className="px-6 py-4">Status</th>
                                        <th className="px-6 py-4 text-right">Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {posts.map((p) => (
                                        <tr key={p.id} className="border-t border-dr-silver">
                                            <td className="px-6 py-4 font-display font-semibold text-dr-navy">{p.title}</td>
                                            <td className="px-6 py-4 text-dr-dark/70">{p.category}</td>
                                            <td className="px-6 py-4 text-dr-dark/70">{p.author}</td>
                                            <td className="px-6 py-4">
                                                <span className={`px-3 py-1 rounded-full text-xs ${p.published ? "bg-dr-blue/15 text-dr-navy" : "bg-dr-silver/50 text-dr-dark"}`}>
                                                    {p.published ? "Published" : "Draft"}
                                                </span>
                                            </td>
                                            <td className="px-6 py-4 text-right">
                                                <button data-testid={`admin-edit-${p.id}`} onClick={() => startEdit(p)} className="inline-flex items-center gap-1.5 mr-3 text-dr-navy hover:text-dr-blue">
                                                    <Edit3 className="w-4 h-4" /> Edit
                                                </button>
                                                <button data-testid={`admin-delete-${p.id}`} onClick={() => remove(p)} className="inline-flex items-center gap-1.5 text-red-600 hover:text-red-700">
                                                    <Trash2 className="w-4 h-4" /> Delete
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                                    {posts.length === 0 && (
                                        <tr><td colSpan={5} className="px-6 py-14 text-center text-dr-dark/60">No posts yet.</td></tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </>
                )}

                {isPost && editing && (
                    <div className="bg-white border border-dr-silver rounded-2xl p-8">
                        <div className="flex justify-between items-center mb-6">
                            <h2 className="font-display text-2xl font-bold text-dr-navy">
                                {editing.mode === "create" ? "New" : "Edit"} {tab === "blog" ? "insight" : "news item"}
                            </h2>
                            <button onClick={cancel} data-testid="admin-editor-cancel" className="text-dr-dark/70 hover:text-dr-navy">
                                <X className="w-5 h-5" />
                            </button>
                        </div>

                        <div className="grid md:grid-cols-2 gap-5">
                            <Field label="Title" required value={editing.form.title} onChange={(v) => setEditing({ ...editing, form: { ...editing.form, title: v } })} testid="editor-title" />
                            <Field label="Category" required value={editing.form.category} onChange={(v) => setEditing({ ...editing, form: { ...editing.form, category: v } })} testid="editor-category" />
                            <Field label="Author" required value={editing.form.author} onChange={(v) => setEditing({ ...editing, form: { ...editing.form, author: v } })} testid="editor-author" />
                            <Field label="Cover image URL" value={editing.form.cover_image || ""} onChange={(v) => setEditing({ ...editing, form: { ...editing.form, cover_image: v } })} testid="editor-cover" />
                            <Field label="Read minutes" type="number" value={editing.form.read_minutes} onChange={(v) => setEditing({ ...editing, form: { ...editing.form, read_minutes: parseInt(v || 1) } })} testid="editor-minutes" />
                            <div className="flex items-end">
                                <label className="inline-flex items-center gap-2">
                                    <input
                                        data-testid="editor-published"
                                        type="checkbox"
                                        checked={!!editing.form.published}
                                        onChange={(e) => setEditing({ ...editing, form: { ...editing.form, published: e.target.checked } })}
                                        className="w-4 h-4"
                                    />
                                    <span className="text-sm text-dr-navy">Published</span>
                                </label>
                            </div>
                        </div>

                        <div className="mt-5">
                            <label className="text-xs uppercase tracking-[0.24em] text-dr-dark/60 font-medium">Excerpt *</label>
                            <textarea
                                data-testid="editor-excerpt"
                                rows={2}
                                value={editing.form.excerpt}
                                onChange={(e) => setEditing({ ...editing, form: { ...editing.form, excerpt: e.target.value } })}
                                className="mt-2 w-full p-3 rounded-lg border border-dr-silver focus:border-dr-blue outline-none"
                            />
                        </div>
                        <div className="mt-5">
                            <label className="text-xs uppercase tracking-[0.24em] text-dr-dark/60 font-medium">Content *</label>
                            <textarea
                                data-testid="editor-content"
                                rows={10}
                                value={editing.form.content}
                                onChange={(e) => setEditing({ ...editing, form: { ...editing.form, content: e.target.value } })}
                                className="mt-2 w-full p-3 rounded-lg border border-dr-silver focus:border-dr-blue outline-none font-mono text-sm"
                                placeholder="Separate paragraphs with a blank line."
                            />
                        </div>

                        <div className="mt-6 flex gap-3">
                            <button
                                data-testid="admin-editor-save"
                                onClick={save}
                                className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-dr-navy text-white font-medium hover:bg-dr-blue transition-colors"
                            >
                                <Save className="w-4 h-4" /> Save
                            </button>
                            <button
                                onClick={cancel}
                                className="inline-flex items-center gap-2 px-6 py-3 rounded-full border border-dr-silver text-dr-navy hover:bg-dr-grey transition-colors"
                            >
                                Cancel
                            </button>
                        </div>
                    </div>
                )}

                {tab === "enquiries" && (
                    <div className="bg-white border border-dr-silver rounded-2xl overflow-hidden">
                        <table className="w-full text-sm">
                            <thead>
                                <tr className="text-left bg-dr-grey/60 text-xs uppercase tracking-widest text-dr-dark/60">
                                    <th className="px-6 py-4">Received</th>
                                    <th className="px-6 py-4">Name</th>
                                    <th className="px-6 py-4">Company</th>
                                    <th className="px-6 py-4">Email</th>
                                    <th className="px-6 py-4">Service</th>
                                    <th className="px-6 py-4">Message</th>
                                </tr>
                            </thead>
                            <tbody>
                                {enquiries.map((e) => (
                                    <tr key={e.id} className="border-t border-dr-silver align-top">
                                        <td className="px-6 py-4 text-dr-dark/70 whitespace-nowrap">{new Date(e.created_at).toLocaleString("en-GB")}</td>
                                        <td className="px-6 py-4 text-dr-navy font-medium">{e.name}</td>
                                        <td className="px-6 py-4 text-dr-dark/70">{e.company || "-"}</td>
                                        <td className="px-6 py-4"><a href={`mailto:${e.email}`} className="text-dr-navy underline">{e.email}</a></td>
                                        <td className="px-6 py-4 text-dr-dark/70">{e.service || "-"}</td>
                                        <td className="px-6 py-4 text-dr-dark/70 max-w-md">{e.message}</td>
                                    </tr>
                                ))}
                                {enquiries.length === 0 && (
                                    <tr><td colSpan={6} className="px-6 py-14 text-center text-dr-dark/60">No enquiries yet.</td></tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                )}

                {tab === "newsletter" && (
                    <div className="bg-white border border-dr-silver rounded-2xl overflow-hidden">
                        <table className="w-full text-sm">
                            <thead>
                                <tr className="text-left bg-dr-grey/60 text-xs uppercase tracking-widest text-dr-dark/60">
                                    <th className="px-6 py-4">Subscribed</th>
                                    <th className="px-6 py-4">Email</th>
                                </tr>
                            </thead>
                            <tbody>
                                {subs.map((s) => (
                                    <tr key={s.id} className="border-t border-dr-silver">
                                        <td className="px-6 py-4 text-dr-dark/70">{new Date(s.created_at).toLocaleString("en-GB")}</td>
                                        <td className="px-6 py-4 text-dr-navy">{s.email}</td>
                                    </tr>
                                ))}
                                {subs.length === 0 && (
                                    <tr><td colSpan={2} className="px-6 py-14 text-center text-dr-dark/60">No subscribers yet.</td></tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </div>
    );
}

function Field({ label, value, onChange, required, type = "text", testid }) {
    return (
        <div>
            <label className="text-xs uppercase tracking-[0.24em] text-dr-dark/60 font-medium">
                {label}{required && " *"}
            </label>
            <input
                data-testid={testid}
                type={type}
                value={value}
                onChange={(e) => onChange(e.target.value)}
                className="mt-2 w-full p-3 rounded-lg border border-dr-silver focus:border-dr-blue outline-none"
            />
        </div>
    );
}
